import React, { useState, useRef, useEffect } from 'react';

const KeywordModal = ({ isOpen, onClose, onDescriptionGenerated, useCase }) => {
  const [keywords, setKeywords] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const inputRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const validateKeyword = (value) => {
    return /^[a-zA-Z]+$/.test(value);
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setError('');
  };

  const addKeyword = () => {
    const trimmedValue = inputValue.trim();
    
    if (!trimmedValue) return;
    
    if (!validateKeyword(trimmedValue)) {
      setError('Only letters (a-z, A-Z) are allowed');
      return;
    }

    if (!keywords.includes(trimmedValue)) {
      setKeywords([...keywords, trimmedValue]);
    }
    
    setInputValue('');
    setError('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      addKeyword();
    }
  };

  const removeKeyword = (keywordToRemove) => {
    setKeywords(keywords.filter(k => k !== keywordToRemove));
  };

  const handleGenerate = async () => {
    if (keywords.length === 0) {
      setError('Please add at least one keyword');
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      const systemPrompt = `You are an expert at crafting concise, clear, and engaging descriptions for Microsoft Copilot agent creation. Your task is to generate a description for the agent's purpose and functionality based solely on a list of keywords. The description should be professional, coherent, and tailored to the agent's intended role, using the keywords to shape its tone and focus.
Create a description of 50-100 words that seamlessly incorporates the provided keywords. Ensure the description is specific, avoids redundancy, and highlights the agent's capabilities and purpose. Use natural language to connect the keywords, forming a cohesive narrative that aligns with the agent's role in Microsoft Copilot. Do not include any placeholders or external references beyond the keywords.
Generate the description using these keywords: ${keywords.join(', ')}`;

      const userPrompt = `Generate a description for a Microsoft Copilot agent tailored for ${useCase} using the provided keywords.`;

      const requestBody = JSON.stringify({
        task: 'Generate Description',
        context: `${systemPrompt}\n\n${userPrompt}`
      });

      console.log('Sending request to API:', {
        endpoint: 'https://api2.prompteh.com/',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_API_KEY ? '[API_KEY_SET]' : '[API_KEY_NOT_SET]'
        },
        body: requestBody
      });

      const response = await fetch('https://api2.prompteh.com/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_API_KEY
        },
        body: requestBody
      });

      console.log('API Response received:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries([...response.headers.entries()])
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error response content:', errorText);
        let errorMessage = `Error: ${response.status} ${response.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error?.message || errorMessage;
        } catch (e) {
          console.error('Failed to parse error response as JSON:', e);
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('API Response data:', data);

      // Extract just the description part from the response
      const generatedDescription = data.description;
      
      console.log('Extracted description:', generatedDescription);
      
      onDescriptionGenerated(generatedDescription);
      onClose();
    } catch (error) {
      setError('Failed to generate description. Please try again.');
      console.error('Generation error:', error.message, error.stack);
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
      <div 
        ref={modalRef}
        className="bg-white rounded-lg p-6 w-full max-w-lg shadow-xl"
      >
        <h3 className="text-xl font-semibold mb-4">Generate Description with Keywords</h3>
        
        <div className="mb-4">
          <label className="block text-gray-700 mb-2">
            Enter keywords (letters only)
          </label>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={addKeyword}
            className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0078D4]"
            placeholder="Type and press Enter, Tab, or click outside"
            disabled={isGenerating}
          />
          {error && (
            <p className="text-red-500 text-sm mt-1">{error}</p>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {keywords.map((keyword, index) => (
            <span
              key={index}
              className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center"
            >
              {keyword}
              <button
                onClick={() => removeKeyword(keyword)}
                className="ml-2 text-blue-800 hover:text-red-600 font-bold"
                disabled={isGenerating}
              >
                ×
              </button>
            </span>
          ))}
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={isGenerating}
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            className={`px-4 py-2 bg-gradient-to-r from-[#0078D4] to-[#00A4B4] text-white rounded-lg transition-transform ${
              isGenerating ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'
            }`}
            disabled={isGenerating}
          >
            {isGenerating ? 'Generating...' : 'Generate'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default KeywordModal; 